import { defineMessages, FormattedMessage } from 'react-intl';

import { useContactInfo } from '@/hooks/useContactInfo';
import { INTL_PREFIX } from '@/messages/const';
import { theme } from '@/theme';

import { ReactComponent as GiftIcon } from '@/assets/icons/gift-icon.svg';

export const commonMessages = defineMessages({
  clear: {
    id: 'plp.filtersModal.clearAll',
    description: 'Clear button',
    defaultMessage: 'Clear All',
  },
  reset: {
    id: 'plp.filtersModal.reset',
    description: 'Reset button',
    defaultMessage: 'Reset',
  },
  search: { defaultMessage: 'Search', id: `${INTL_PREFIX.COMMON}.search` },
  subtotal: {
    defaultMessage: 'Subtotal',
    id: 'checkout.shoppingBag.subtotal',
  },
  deliveryFee: {
    defaultMessage: 'Delivery charges',
    id: 'checkout.shoppingBag.deliveryFee',
  },
  deductedAmount: {
    defaultMessage: 'Deducted amount',
    id: 'deductedAmount',
  },
  free: {
    defaultMessage: 'Free',
    id: 'checkout.shoppingBag.free',
  },
  salesTax: {
    defaultMessage: 'Sales tax',
    id: 'checkout.shoppingBag.salesTax',
  },
  vatIncluded: {
    defaultMessage: 'Vat included',
    id: 'checkout.shoppingBag.vatIncluded',
  },
  retailDeliveryFees: {
    defaultMessage: 'CO Retail Delivery Fees',
    id: `${INTL_PREFIX.COMMON}.retailDeliveryFees`,
  },
  deposit: {
    defaultMessage: 'Deposit',
    id: 'common.deposit',
  },
  alreadyPaidDeposit: {
    defaultMessage: 'Already paid deposit',
    id: 'alreadyPaidDeposit',
  },
  payNowDeposit: {
    defaultMessage: 'Pay now deposit',
    id: 'checkout.shoppingBag.payNowDeposit',
  },
  total: {
    defaultMessage: 'Total',
    id: 'checkout.shoppingBag.total',
  },
  home: {
    defaultMessage: 'Home',
    id: 'home',
  },
  watches: {
    defaultMessage: 'Watches',
    id: 'common.watches',
  },
  allTheWatches: {
    defaultMessage: 'All the watches',
    id: 'allTheWatches',
  },
  theCollections: {
    defaultMessage: 'Collections',
    id: 'theCollections',
  },
  straps: {
    defaultMessage: 'Straps',
    id: 'common.straps',
  },
  packs: {
    defaultMessage: 'Packs',
    id: 'common.packs',
  },
  addToBag: {
    defaultMessage: 'Add to bag',
    id: 'cta.addToBag',
  },
  addAStrap: {
    defaultMessage: 'Add a strap',
    id: 'checkout.shoppingBag.addStrap',
  },
  addAWarrantyDurationTwo: {
    defaultMessage: 'two',
    id: 'addAWarranty.durationTwo',
  },
  addAWarrantyDurationThree: {
    defaultMessage: 'three',
    id: 'addAWarranty.durationThree',
  },
  warrantyExtensionCampaign: {
    defaultMessage:
      'This watch is eligible for a complimentary warranty extension.',
    id: 'warrantyExtensionCampaign',
  },
  addAnAdditionalStrap: {
    defaultMessage: 'Add an additional strap',
    id: 'checkout.shoppingBag.addAddtionalStrap',
  },
  notifyMe: {
    defaultMessage: 'Notify me',
    id: 'cta.notifyMe',
  },
  contactUs: {
    defaultMessage: 'Contact us',
    id: 'cta.contactUs',
  },
  availabilityRequest: {
    defaultMessage: 'Availability request',
    id: 'availabilityRequest',
  },
  preOrderNow: {
    defaultMessage: 'Pre-order now',
    id: 'cta.preorderNow',
  },
  callToBuy: {
    defaultMessage: 'Call to buy',
    id: 'cta.callToBuy',
  },
  callToPreOrder: {
    defaultMessage: 'Call to pre order',
    id: 'callToPreOrder',
  },
  bookAppointment: {
    defaultMessage: 'Book an appointment',
    id: 'cta.bookAnAppointment',
  },
  review: {
    defaultMessage: 'Review',
    id: 'review',
  },
  youMayAlsoLike: {
    defaultMessage: 'You may also like',
    id: 'youMayAlsoLike',
  },
  findOutMore: {
    defaultMessage: 'Find out more',
    id: 'findOutMore',
  },
  buckleNotIncluded: {
    defaultMessage: 'Buckle not included',
    id: 'wishlist.buckleNotIncluded',
  },
  newest: {
    defaultMessage: 'Newest',
    description: 'One of the sorting options on Watch PLP',
    id: 'plp.allWatchesSort.newest',
  },
  priceAsc: {
    defaultMessage: 'Price low-high',
    description: 'One of the sorting options on Watch PLP',
    id: 'plp.allWatchesSort.priceLowHigh',
  },
  priceDesc: {
    defaultMessage: 'Price high-low',
    description: 'One of the sorting options on Watch PLP',
    id: 'plp.allWatchesSort.priceHighLow',
  },
  checkOut: {
    defaultMessage: 'Check out',
    id: `checkout.section.header`,
  },
  checkout: {
    defaultMessage: 'Checkout',
    id: `checkout.shoppingBag.checkout`,
  },
  logIn: {
    defaultMessage: 'Log in',
    id: `account.login.cta`,
  },
  signIn: {
    defaultMessage: 'Sign in',
    id: `checkout.signIn.title`,
  },
  exclSalesTax: {
    defaultMessage: 'Excl. Sales Tax.',
    id: `${INTL_PREFIX.COMMON}.exclSalesTax`,
  },
  vatIncl: {
    defaultMessage: 'VAT Incl.',
    id: `${INTL_PREFIX.COMMON}.vatIncl`,
  },
  shippingAddress: {
    defaultMessage: 'Shipping address',
    id: `checkout.delivery.shippingAddress`,
  },
  billingAddress: {
    defaultMessage: 'Billing address',
    id: `checkout.delivery.billingAddress`,
  },
  deliverySchedule: {
    defaultMessage: 'Delivery schedule',
    id: `${INTL_PREFIX.COMMON}.deliverySchedule`,
  },
  monFri: {
    id: 'checkout.delivery.scheduleLabel.monFri',
    defaultMessage: 'Mon-Fri',
  },
  monSat: {
    id: 'checkout.delivery.scheduleLabel.monSat',
    defaultMessage: 'Mon-Sat',
  },
  deliveryScheduleDescriptionForBoutiqueDelivery: {
    defaultMessage: 'Upon notification from boutique',
    id: `${INTL_PREFIX.COMMON}.deliveryScheduleDescriptionForBoutiqueDelivery`,
  },
  deliveryScheduleDescriptionForHomeDelivery: {
    defaultMessage:
      'Once shipped, order should be delivered within 2-3 business days.',
    id: `${INTL_PREFIX.COMMON}.deliveryScheduleDescriptionForHomeDelivery`,
  },
  shoppingBag: {
    defaultMessage: 'Shopping bag',
    id: `${INTL_PREFIX.COMMON}.shoppingBag`,
  },
  yourShoppingBagIsEmpty: {
    defaultMessage: 'Your shopping bag is empty',
    id: `${INTL_PREFIX.COMMON}.yourShoppingBagIsEmpty`,
  },
  freeShippingAndReturns: {
    defaultMessage: 'Free shipping and returns',
    id: `${INTL_PREFIX.COMMON}.freeShippingAndReturns`,
  },
  continueShopping: {
    defaultMessage: 'Continue shopping',
    id: `checkout.shoppingBag.previous`,
  },
  select: {
    defaultMessage: 'Select',
    id: `${INTL_PREFIX.COMMON}.select`,
  },
  remove: {
    id: `checkout.shoppingBag.remove`,
    defaultMessage: 'Remove',
  },
  applyAVoucher: {
    id: `checkout.shoppingBag.voucher`,
    defaultMessage: 'Apply a voucher',
  },
  errorPromoCode: {
    id: `checkout.shoppingBag.errorPromoCode`,
    defaultMessage: 'Promo code is invalid',
  },
  enterCode: {
    id: `checkout.shoppingBag.applyAVoucher.input`,
    defaultMessage: 'Enter code',
  },
  allTransactionsAreSafeAndSecure: {
    id: `checkout.shoppingBag.securityNotice`,
    defaultMessage: 'All transactions are safe and secure',
  },
  order: {
    defaultMessage: 'Order',
    id: `${INTL_PREFIX.COMMON}.order`,
  },
  orderSummary: {
    defaultMessage: 'Order summary',
    id: `${INTL_PREFIX.COMMON}.orderSummary`,
  },
  orderPlaced: {
    defaultMessage: 'Order placed',
    id: `${INTL_PREFIX.COMMON}.orderPlaced`,
  },
  thankYou: {
    defaultMessage: 'Thank you',
    id: `${INTL_PREFIX.COMMON}.thankYou`,
  },
  thisIsAGift: {
    defaultMessage: 'This is a gift',
    id: `checkout.delivery.gift`,
  },
  from: {
    defaultMessage: 'From',
    id: `checkout.delivery.gift.from`,
  },
  to: {
    defaultMessage: 'To',
    id: `checkout.delivery.gift.to`,
  },
  composeYourMessage: {
    defaultMessage: 'Compose your message',
    id: `checkout.delivery.gift.message`,
  },
  cancel: {
    defaultMessage: 'Cancel',
    id: `cta.cancel`,
  },
  confirm: {
    defaultMessage: 'Confirm',
    id: `service.confirm`,
  },
  noResultsFound: {
    defaultMessage: 'No results found',
    id: `${INTL_PREFIX.COMMON}.noResultsFound`,
  },
  save: {
    defaultMessage: 'Save',
    id: `cta.save`,
  },
  discoverTheCollection: {
    defaultMessage: 'Discover the collection',
    id: `${INTL_PREFIX.COMMON}.discoverTheCollection`,
  },
  filters: {
    defaultMessage: 'Filters',
    id: `plp.allWatchesFilter.title`,
  },
  change: {
    defaultMessage: 'Change',
    id: `cta.change`,
  },
  promoCodeInvalid: {
    defaultMessage: 'Promo code is invalid',
    id: `${INTL_PREFIX.COMMON}.promoCodeInvalid`,
  },
  bestseller: {
    defaultMessage: 'Bestseller',
    id: `${INTL_PREFIX.COMMON}.bestseller`,
  },
  apply: {
    defaultMessage: 'Apply',
    id: `${INTL_PREFIX.COMMON}.apply`,
  },
  loading: {
    defaultMessage: 'Loading',
    id: `${INTL_PREFIX.COMMON}.loading`,
  },
  process: {
    defaultMessage: 'Process',
    id: `${INTL_PREFIX.COMMON}.process`,
  },
  contactInformation: {
    defaultMessage: 'Contact information',
    id: `checkout.reviewOrder.contactInformation`,
  },
  next: {
    defaultMessage: 'Next',
    id: `common.next`,
  },
  niceToHave: {
    id: 'common.niceToHave',
    defaultMessage: 'Nice to have',
  },
  submit: {
    id: 'common.submit',
    defaultMessage: 'Submit',
  },
  conditionsOfSaleRequired: {
    defaultMessage: 'Please accept the Breitling Conditions of Sale.',
    id: `${INTL_PREFIX.COMMON}.conditionsOfSaleRequired`,
  },
  areYouSure: {
    defaultMessage: 'Are you sure?',
    id: `checkout.modal.header.areYouSure`,
  },
  ok: {
    defaultMessage: 'Ok',
    id: `cta.ok`,
  },
  loadingMore: {
    defaultMessage: 'loading More',
    id: `cta.loadingMore`,
  },
  moreResults: {
    id: `${INTL_PREFIX.COMMON}.moreResults`,
    defaultMessage: 'More results',
  },
  toPayLater: {
    id: `checkout.shoppingBag.toPayLater`,
    defaultMessage: 'To pay later',
  },
  toPayNow: {
    id: `checkout.shoppingBag.toPayNow`,
    defaultMessage: 'To pay now',
  },
  finalPayment: {
    id: `checkout.shoppingBag.finalPayment`,
    defaultMessage: 'Final payment',
  },
  firstDepositPayment: {
    id: `checkout.shoppingBag.firstDepositPayment`,
    defaultMessage: 'First deposit payment',
  },
  myWatches: {
    defaultMessage: 'My Watches',
    id: `${INTL_PREFIX.COMMON}.myWatches`,
  },
  learnMore: {
    defaultMessage: 'Learn more',
    id: `${INTL_PREFIX.COMMON}.learnMore`,
  },
  back: {
    defaultMessage: 'Back',
    id: `account.navigation.back`,
  },
  accountDetailsTitle: {
    defaultMessage: 'My details',
    id: `account.details.title`,
  },
  breitlingPrivacyPolicy: {
    defaultMessage: 'Breitling Privacy Policy',
    id: `${INTL_PREFIX.COMMON}.breitlingPrivacyPolicy`,
  },
  breitlingTermsOfUse: {
    defaultMessage: 'Breitling terms of use',
    id: `${INTL_PREFIX.COMMON}.breitlingTermsOfUse`,
  },
  breitlingTermsOfUseAndAdditionalServices: {
    defaultMessage:
      'Breitling terms of use and accepted the Terms and Conditions – Additional Services',
    id: `${INTL_PREFIX.COMMON}.breitlingTermsOfUseAndAdditionalServices`,
  },
  termsOfUse: {
    defaultMessage: 'Terms of Use',
    id: `${INTL_PREFIX.COMMON}.termsOfUse`,
  },
  here: {
    defaultMessage: 'here',
    id: `${INTL_PREFIX.COMMON}.here`,
  },
  day: {
    defaultMessage: 'Day',
    id: `${INTL_PREFIX.COMMON}.day`,
  },
  month: {
    defaultMessage: 'Month',
    id: `${INTL_PREFIX.COMMON}.month`,
  },
  year: {
    defaultMessage: 'Year',
    id: `${INTL_PREFIX.COMMON}.year`,
  },
  logOut: {
    defaultMessage: 'Log out',
    id: `account.logOut`,
  },
  gifting: {
    defaultMessage: 'Gifting',
    id: `${INTL_PREFIX.COMMON}.gifting`,
  },
  customerContactInformation: {
    defaultMessage: 'Customer contact information',
    id: `${INTL_PREFIX.COMMON}.customerContactInformation`,
  },
  welcomeToBreitling: {
    defaultMessage: 'Welcome to breitling',
    id: `${INTL_PREFIX.COMMON}.welcomeToBreitling`,
  },
  about: {
    defaultMessage: 'About',
    id: `${INTL_PREFIX.COMMON}.about`,
  },
  new: {
    defaultMessage: 'New',
    id: 'common.new',
  },
  boutiqueAndOnlineExclusive: {
    defaultMessage: 'Boutique & Online Exclusive',
    id: 'boutiqueAndOnlineExclusive',
  },
  boutiqueExclusive: {
    defaultMessage: 'Boutique Exclusive',
    id: 'boutiqueExclusive',
  },
  onlineExclusive: {
    defaultMessage: 'Online Exclusive',
    id: 'onlineExclusive',
  },
  countryExclusive: {
    defaultMessage: 'Country Exclusive',
    id: 'countryExclusive',
  },
  limitedEdition: {
    defaultMessage: 'Limited',
    id: 'limitedEdition',
  },
  capsule: {
    defaultMessage: 'Capsule',
    id: 'capsule',
  },
  reference: {
    defaultMessage: 'Model reference *',
    id: `${INTL_PREFIX.COMMON}.reference`,
  },
  serialNumber: {
    defaultMessage: 'Serial number *',
    id: `${INTL_PREFIX.COMMON}.serialNumber`,
  },
  goToMyAccount: {
    defaultMessage: 'Go to my account',
    id: `${INTL_PREFIX.COMMON}.goToMyAccount`,
  },
  priceVatNotice: {
    defaultMessage: `All displayed prices are Breitling non binding public recommended retail prices, {taxMessage} when relevant. Prices and model selection do not constitute a binding offer or contract and are therefore subject to change at any time, prior to order confirmation.`,
    id: 'price.vat.notice',
  },
  priceVatNoticePart1: {
    defaultMessage: `All displayed prices are Breitling non binding public recommended retail prices, {taxMessage} when relevant.`,
    id: 'price.vatNotice1',
  },
  priceVatNoticePart2: {
    defaultMessage: `Prices and model selection do not constitute a binding offer or contract and are therefore subject to change at any time, prior to order confirmation.`,
    id: 'price.vatNotice2',
  },
  contactUsNotice: {
    defaultMessage: 'CONTACT US AT (647) 691-1073',
    id: `${INTL_PREFIX.COMMON}.contactUsNotice`,
  },
  all: {
    defaultMessage: 'All',
    id: `${INTL_PREFIX.COMMON}.all`,
  },
  watch: {
    defaultMessage: 'Watch',
    id: `common.watch`,
  },
  strap: {
    defaultMessage: 'Strap',
    id: `common.strap`,
  },
  nextPage: {
    defaultMessage: 'Next Page',
    id: `nextPage`,
  },
  store: {
    defaultMessage: 'Store',
    id: `common.store`,
  },
  stores: {
    defaultMessage: 'Stores',
    id: `common.stores`,
  },
  close: {
    defaultMessage: 'Close',
    id: `common.close`,
  },
  extraBuckle: {
    defaultMessage: 'Get an extra buckle for your strap',
    id: `${INTL_PREFIX.COMMON}.extraBuckle`,
  },
  promoApplied: {
    defaultMessage: 'exclusive offer',
    id: `${INTL_PREFIX.COMMON}.promoApplied`,
  },
  sendGiftHeader: {
    id: 'sendGiftHeader',
    defaultMessage: 'Send as a gift',
  },
  sendGiftDescription: {
    id: 'sendGiftDescription',
    defaultMessage:
      'With 4Gift, you can send a Breitling gift individually or as a group, and time its arrival to a special occasion.',
  },
  sendGiftSender: {
    id: 'sendGiftSender',
    defaultMessage: 'THE SENDER',
  },
  sendGiftGiftDigitally: {
    id: 'sendGiftGiftDigitally',
    defaultMessage: 'Gift it digitally',
  },
  sendGiftGiftDigitallyDesc: {
    id: 'sendGiftGiftDigitallyDesc',
    defaultMessage:
      "Personalize the recipient's virtual gift by opening with a note or video.",
  },
  sendGiftPlanGift: {
    id: 'sendGiftPlanGift',
    defaultMessage: 'Plan your gift',
  },
  sendGiftPlanGiftDesc: {
    id: 'sendGiftPlanGiftDesc',
    defaultMessage:
      'Split costs or send on your own. Choose instant delivery or a scheduled date.',
  },
  sendGiftRecipient: {
    id: 'sendGiftRecipient',
    defaultMessage: 'THE RECIPIENT',
  },
  sendGiftAdjustGifts: {
    id: 'sendGiftAdjustGifts',
    defaultMessage: 'Adjust the gifts',
  },
  sendGiftAdjustGiftsDesc: {
    id: 'sendGiftAdjustGiftsDesc',
    defaultMessage:
      'Select details like dial color and strap within the model you were gifted.',
  },
  sendGiftExchangeGift: {
    id: 'sendGiftExchangeGift',
    defaultMessage: 'Exchange the gift',
  },
  sendGiftExchangeGiftDesc: {
    id: 'sendGiftExchangeGiftDesc',
    defaultMessage:
      'Swap it out entirely for something different that better suits.',
  },
  sendGiftGetStarted: {
    id: 'sendGiftGetStarted',
    defaultMessage: 'Get started',
  },
  sendGiftLearnMore: {
    id: 'sendGiftLearnMore',
    defaultMessage: 'Learn More',
  },
  sendGiftGiveUs: {
    id: 'sendGiftGiveUs',
    defaultMessage: 'Give us',
  },
  sendGiftGift: {
    id: 'sendGiftGift',
    defaultMessage: 'Gift',
  },
  service: {
    id: 'service.label',
    defaultMessage: 'Service',
  },
  contactService: {
    id: 'service.contact',
    defaultMessage: 'Contact',
  },
  callBreitlingTitle: {
    id: 'service.contact.callBreitlingTitle',
    defaultMessage: 'Call Breitling',
  },
  callBreitlingText: {
    id: 'service.contact.callBreitlingText',
    defaultMessage:
      'To receive more information on your desired product or questions on your product order.',
  },
  chatWithUsTitle: {
    id: 'service.contact.chatWithUsTitle',
    defaultMessage: 'Chat with us',
  },
  chatWithUsText: {
    id: 'service.contact.chatWithUsText',
    defaultMessage:
      'A Breitling ambassador will reply to you immediately during business hours.',
  },
  emailUsTitle: {
    id: 'service.contact.emailUsTitle',
    defaultMessage: 'Write us an email',
  },
  emailUsText: {
    id: 'service.contact.emailUsText',
    defaultMessage: 'Our Concierge team will respond within 2 working days.',
  },
  requestAppointmentTitle: {
    id: 'service.contact.requestAppointmentTitle',
    defaultMessage: 'Request an appointment',
  },
  requestAppointmentText: {
    id: 'service.contact.requestAppointmentText',
    defaultMessage:
      'Our Breitling sales associates are looking forward to welcoming you in our Breitling boutiques.',
  },
  findBoutiqueTitle: {
    id: 'service.contact.findBoutiqueTitle',
    defaultMessage: 'Find a boutique',
  },
  findBoutiqueText: {
    id: 'service.contact.findBoutiqueText',
    defaultMessage:
      'Discover your desired Breitling product in your nearest Breitling boutique or at retailer close to you.',
  },
  faqTitle: {
    id: 'service.contact.faqTitle',
    defaultMessage: 'FAQ',
  },
  faqText: {
    id: 'service.contact.faqText',
    defaultMessage:
      'Visit our FAQs for Breitling products and services enquiries.',
  },
  tracking: {
    id: 'tracking',
    defaultMessage: 'Tracking',
  },
  myOrders: {
    defaultMessage: 'My orders',
    id: `myaccount.myorders`,
  },
  myaccount: {
    defaultMessage: 'My account',
    id: `myaccount`,
  },
  selectStrap: {
    defaultMessage: 'Please select a strap from each of the available options.',
    id: 'selectStrap',
  },
  selectAvailableStrapSize: {
    defaultMessage: 'Please choose from the available strap sizes.',
    id: 'selectAvailableStrapSize',
  },
  outOfStock: {
    defaultMessage: 'Out of stock',
    id: 'outOfStock',
  },
  defaultCopyRight: {
    defaultMessage: '© {year} Breitling. All rights reserved.',
    id: 'defaultCopyRight',
  },
  pdpBoutiqueDeliveryAvailableLabel: {
    defaultMessage: 'Boutique delivery available',
    id: 'pdp.boutiqueDeliveryAvailableLabel',
  },
  pdpBoutiqueDeliveryAvailableModal: {
    defaultMessage:
      'You can choose to collect your purchase from the nearest Breitling boutique (a convenient option when you can\'t be home to sign for your parcel). Select "Boutique Delivery" at checkout, then the location that best suits.',
    id: 'pdp.boutiqueDeliveryAvailableModal',
  },
  printText: {
    defaultMessage: 'Print the document',
    id: 'common.printText',
  },
  accessibilityFeatures: {
    defaultMessage: 'Accessibility features',
    id: `${INTL_PREFIX.COMMON}.accessibilityFeatures`,
  },
  skipToContent: {
    defaultMessage: 'Skip to content',
    id: `${INTL_PREFIX.COMMON}.skipToContent`,
  },
  mondayToFriday: {
    defaultMessage: 'Mon-Fri',
    id: `common.monToFri`,
  },
  saturday: {
    defaultMessage: 'Sat',
    id: `common.saturday`,
  },
  sunday: {
    defaultMessage: 'Sun',
    id: `common.sunday`,
  },
  saturdayToSunday: {
    defaultMessage: 'Sat-Sun',
    id: `common.satToSun`,
  },
  preorderAffirm: {
    defaultMessage:
      'Benefit from split payment and 0% financing up to 24 months on the remaining amount after your pre-order deposit.',
    id: 'preorderAffirm',
  },
  invalidAffirmAmount: {
    defaultMessage:
      'Benefit from split payment and 0% financing up to 24 months.',
    id: 'invalidAffirmAmount',
  },
  readMore: {
    id: 'common.readMore',
    defaultMessage: 'Read more',
  },
  readLess: {
    id: 'common.readLess',
    defaultMessage: 'Read less',
  },
  waterResistance: {
    id: 'common.waterResistance',
    defaultMessage: 'Water resistance',
  },
  shareEmailCheckout: {
    id: `${INTL_PREFIX.COMMON}.shareEmailCheckout`,
    defaultMessage: 'Check this out - ',
  },
  payIn: {
    id: 'payment.oney.payIn',
    defaultMessage: 'Pay in',
  },
  agosMessage: {
    id: 'payment.agos.agosMessage',
    defaultMessage: 'Payment in installments with 0% interest possible with',
  },
  agosCalculateFinancing: {
    id: 'payment.agos.agosCalculateFinancing',
    defaultMessage: 'Calculate financing with Agos',
  },
  privacyPolicy: {
    id: `${INTL_PREFIX.COMMON}.privacyPolicy`,
    defaultMessage: 'Privacy Policy',
  },
  additionalCembraMessage: {
    id: `payment.cembra.additionalCembraMessage`,
    defaultMessage:
      'Benefit from split payment and 0% financing up to 48 months.',
  },
  commonSubmittedSuccessfully: {
    id: `${INTL_PREFIX.COMMON}.commonSubmittedSuccessfully`,
    defaultMessage: 'Submitted successfully.',
  },
  discover: {
    id: `${INTL_PREFIX.COMMON}.discover`,
    defaultMessage: 'Discover',
  },
  showMore: {
    id: `${INTL_PREFIX.COMMON}.showMore`,
    defaultMessage: 'Show more',
  },
  showLess: {
    id: `${INTL_PREFIX.COMMON}.showLess`,
    defaultMessage: 'Show less',
  },
  yes: {
    id: 'faq.yesCta',
    defaultMessage: 'Yes',
  },
  no: {
    id: 'faq.noCta',
    defaultMessage: 'No',
  },
  latestArticles: {
    id: 'news.latestArticles',
    defaultMessage: 'Latest articles',
  },
  news: {
    id: 'news.title',
    defaultMessage: 'News',
  },
  newsTags: {
    id: 'news.newsTags',
    defaultMessage: 'All the news',
  },
  eyewear: {
    defaultMessage: 'Sunglasses',
    id: 'common.eyewear',
  },
  eyewears: {
    defaultMessage: 'Sunglasses',
    id: 'common.eyewears',
  },
  discoverAllNews: {
    id: 'news.discoverAllNews',
    defaultMessage: 'discover all news',
  },
  genericError: {
    id: 'genericError',
    defaultMessage: 'An unexpected error occurred. Please try again.',
  },
  backToList: {
    defaultMessage: 'Back to List',
    id: `${INTL_PREFIX.COMMON}.backToList`,
  },
  ourSelection: {
    defaultMessage: 'Our Selection',
    id: `${INTL_PREFIX.COMMON}.ourSelection`,
  },
  newWatches: {
    defaultMessage: 'New Watches',
    id: `${INTL_PREFIX.COMMON}.newWatches`,
  },
  watchesFound: {
    defaultMessage: 'Watches Found',
    id: `${INTL_PREFIX.COMMON}.watchesFound`,
  },
  unableToLoadProductDetails: {
    defaultMessage: 'Unable to load product details. Please try again later.',
    id: `${INTL_PREFIX.COMMON}.unableToLoadProductDetails`,
  },
  sunglasses: {
    defaultMessage: 'Sunglasses', // Use the same as for "Eyewear", except for EN
    id: 'common.eyewear',
  },
  days: {
    defaultMessage: 'Days',
    id: 'common.days',
  },
  hours: {
    defaultMessage: 'Hours',
    id: 'common.hours',
  },
  minutes: {
    defaultMessage: 'Minutes',
    id: 'common.minutes',
  },
  seconds: {
    defaultMessage: 'Seconds',
    id: 'common.seconds',
  },
});
export const OrdinalMessage = (num?: number) => {
  if (num && num >= 1 && num <= 3) {
    const defaultMessages = ['first', 'second', 'third'];
    return (
      <FormattedMessage
        defaultMessage={defaultMessages[num - 1]}
        id={`${INTL_PREFIX.COMMON}.ordinal.${num}`}
      />
    );
  }
};

export const AddAWarranty = (duration?: string) => (
  <FormattedMessage
    defaultMessage='Extend your warranty for another {extensionDuration} years'
    id='addAWarranty'
    values={{
      extensionDuration:
        duration === '2' ? (
          <FormattedMessage {...commonMessages.addAWarrantyDurationTwo} />
        ) : (
          <FormattedMessage {...commonMessages.addAWarrantyDurationThree} />
        ),
    }}
  />
);
export const EditDeliveryInfo = (
  <FormattedMessage
    defaultMessage='To edit delivery information please contact support.'
    id={`${INTL_PREFIX.COMMON}.editDeliveryInfo`}
  />
);

export const DiveIn = (
  <FormattedMessage
    defaultMessage='Dive In'
    id={`${INTL_PREFIX.COMMON}.DiveIn`}
  />
);

export const ContactUsNotice = () => {
  const { phoneNumber } = useContactInfo();

  return (
    <FormattedMessage
      defaultMessage='CONTACT US AT {phoneNumber}'
      id='common.contactUsNotice'
      values={{
        phoneNumber: (
          <a
            href={`tel:${phoneNumber}`}
            style={{ marginLeft: theme.spacing(1) }}
          >
            {phoneNumber}
          </a>
        ),
      }}
    />
  );
};
export const GiveAsAGift = (
  <FormattedMessage
    defaultMessage='Give as a {icon} gift'
    id={`${INTL_PREFIX.COMMON}.GiveAsAGift`}
    values={{
      icon: <GiftIcon />,
    }}
  />
);

export const CembraMessage = (amount: JSX.Element) => (
  <FormattedMessage
    defaultMessage='From {amount} per month with'
    id='payment.cembra.sentence'
    values={{
      amount,
    }}
  />
);

export const OneyDownPaymentMessage = (amount: JSX.Element, times?: number) => (
  <FormattedMessage
    defaultMessage='Down payment of {amount} and {times}x {amount}.'
    id='payment.oney.downPaymentMessage'
    values={{
      amount,
      times: times ?? 2,
    }}
  />
);

export const FixedAprPercent = (percent: number) => (
  <FormattedMessage
    defaultMessage='Fixed APR of {percent}%.'
    id='payment.fixedAprPercent'
    values={{
      percent,
    }}
  />
);

export const PaymentCost = (price: JSX.Element) => (
  <FormattedMessage
    defaultMessage='Cost {price}.'
    id='payment.cost'
    values={{
      price,
    }}
  />
);

export const PreOrderInformationMessage = (
  <FormattedMessage
    defaultMessage='Pre-orders can take an average of two months to receive. We will let you know when your watch is ready to ship.'
    id='preorder.information'
  />
);
